/**
 * ElevationViews — four wall-elevation panels (Wall A back / B right /
 * C front / D left) that complement the plan view in the Size tab.
 *
 * This file is the orchestrator: it derives wall/equipment/tray data from
 * the project, owns the mode toggle (drop vs equipment) + selection
 * reducer, and persists changes back through the shared `update()` patch
 * helper. Per-wall canvas interaction lives in `./elevation/WallPanel`.
 */
import { useCallback, useEffect, useReducer, useState } from "react";
import { toast } from "sonner";
import { ArrowCounterClockwise, ArrowClockwise, CaretDown, PushPin } from "@phosphor-icons/react";
import { genId, WALLS, wallToPlan, positionForWall, wallFlushMargin } from "./elevation/wallGeometry";
import { orientedDimensions } from "@/lib/orientation";
import HistoryRail from "./elevation/HistoryRail";
import WallPanel from "./elevation/WallPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";

const UNDO_LIMIT = 5;

export default function ElevationViews({ project, update, installSide = "all" }) {
  // `_in` fields are always stored as inches. The project's `unit_system`
  // only affects how the SizeTab inputs display them — never the underlying
  // geometry. Earlier the elevation views applied a 39.37 multiplier when
  // metric was active, which scaled the room footprint out from under the
  // equipment + tray data (root cause of the "everything tiny in corner"
  // bug on existing projects after toggling units).
  const L = project.length_in || 0;
  const W = project.width_in  || 0;
  const H = project.depth_in  || 0;
  const openings  = project.wall_openings || [];
  const allLoadList = project.equipment_list || [];
  // v1.99 — installation_side filter also applies to elevation views. The
  // SizeTab toolbar toggle is the single source of truth: Interior shows
  // only interior equipment/trays; Exterior shows only exterior; All
  // shows everything plus the Roof panel.
  const matchesSide = (sideField) => {
    const s = sideField || "interior";
    if (installSide === "all") return true;
    if (installSide === "interior") return s === "interior";
    if (installSide === "exterior") return s !== "interior"; // exterior or through
    return true;
  };
  const equipment = allLoadList.filter((i) => i?.position && matchesSide(i.installation_side));
  const unplaced  = allLoadList.filter((i) => !i?.position);
  const allTrays  = (project.cable_trays || []).filter((t) => matchesSide(t.installation_side));
  const drops     = allTrays.filter((t) => t.kind === "drop");
  const runs      = allTrays.filter((t) => t.kind !== "drop");
  // v1.99 — default installation_side stamped on NEWLY created drops, runs,
  // and equipment placed via this elevation panel. "exterior" mode creates
  // exterior items; "interior" or "all" → interior (the default).
  const newItemSide = installSide === "exterior" ? "exterior" : "interior";
  const trayDefaults = project.tray_defaults || {
    tray_type: "Ladder", width_in: 12, height_in: 4,
    material: "Aluminum", fill_limit_pct: 50,
  };
  const [mode, setMode] = useState("drop");          // "drop" | "equipment"
  const [pickedItemId, setPickedItemId] = useState("");
  // Single-wall multi-select: equipment items on `selection.wallId` are
  // highlighted; shift-click toggles, click-drag on any selected item moves
  // the whole group. Cleared when the mode toggles or Esc is pressed.
  // useReducer used to dodge the `set-state-in-effect` lint rule.
  const [selection, dispatchSelection] = useReducer(
    (_state, action) => {
      if (action === "clear") return { wallId: null, ids: new Set() };
      return action;             // { wallId, ids }
    },
    { wallId: null, ids: new Set() },
  );

  useEffect(() => { dispatchSelection("clear"); }, [mode]);
  const pickedItem = unplaced.find((it) => it.id === pickedItemId) || null;

  // ── Undo / Redo stack (capacity 5 each) ─────────────────────────
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  /** Named layout checkpoints — independent of the rolling undo cap. */
  const [pinnedCheckpoints, setPinnedCheckpoints] = useState([]);
  /** Optional wall id (A/B/C/D) currently maximized to fill the grid. When
   *  set, that one panel renders full-width and the other three are hidden;
   *  a "Restore" button returns to the 2×2 grid. */
  const [maximizedWallId, setMaximizedWallId] = useState(null);
  const snapshot = () => {
    // Defer everything to a macrotask so this entire bundle (clone +
    // Date.now() + setState) runs outside React's render phase. Capture
    // `project` from the current closure synchronously above so the snap
    // reflects the BEFORE state, not whatever the parent re-renders to.
    const projSnapshot = project;
    setTimeout(() => {
      const snap = {
        equipment_list:   (projSnapshot.equipment_list   || []).map((x) => ({ ...x, position: x.position ? { ...x.position } : null })),
        cable_trays: (projSnapshot.cable_trays || []).map((x) => ({ ...x, points: x.points ? x.points.map((p) => [...p]) : x.points })),
      };
      const entry = {
        snap, ts: Date.now(),
        summary: {
          items: (snap.equipment_list   || []).filter((x) => x.position).length,
          trays: (snap.cable_trays || []).length,
        },
      };
      setUndoStack((prev) => [...prev, entry].slice(-UNDO_LIMIT));
      // Any new mutation invalidates the redo stack — branching from
      // an older state would conflict with already-undone changes.
      setRedoStack([]);
    }, 0);
  };
  const applyUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const last = undoStack[undoStack.length - 1];
    const currentSnap = {
      equipment_list:   (project.equipment_list   || []).map((x) => ({ ...x, position: x.position ? { ...x.position } : null })),
      cable_trays: (project.cable_trays || []).map((x) => ({ ...x, points: x.points ? x.points.map((p) => [...p]) : x.points })),
    };
    const redoEntry = { snap: currentSnap, ts: Date.now(),
      summary: {
        items: currentSnap.equipment_list.filter((x) => x.position).length,
        trays: currentSnap.cable_trays.length,
      } };
    setUndoStack((prev) => prev.slice(0, -1));
    setRedoStack((prev) => [...prev, redoEntry].slice(-UNDO_LIMIT));
    update(last.snap);
    toast.success("Reverted last change");
  }, [undoStack, project, update]);
  const applyRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const last = redoStack[redoStack.length - 1];
    const currentSnap = {
      equipment_list:   (project.equipment_list   || []).map((x) => ({ ...x, position: x.position ? { ...x.position } : null })),
      cable_trays: (project.cable_trays || []).map((x) => ({ ...x, points: x.points ? x.points.map((p) => [...p]) : x.points })),
    };
    const undoEntry = { snap: currentSnap, ts: Date.now(),
      summary: {
        items: currentSnap.equipment_list.filter((x) => x.position).length,
        trays: currentSnap.cable_trays.length,
      } };
    setRedoStack((prev) => prev.slice(0, -1));
    setUndoStack((prev) => [...prev, undoEntry].slice(-UNDO_LIMIT));
    update(last.snap);
    toast.success("Re-applied change");
  }, [redoStack, project, update]);
  /** Jump to a specific history index — applies that snapshot and DROPS
   *  every entry from that index onwards (so the user can continue
   *  branching from there without confusion). */
  const jumpToHistory = (idx) => {
    if (idx < 0 || idx >= undoStack.length) return;
    const snap = undoStack[idx].snap;
    setUndoStack((prev) => prev.slice(0, idx));
    setRedoStack([]); // jumping invalidates redo
    update(snap);
    toast.success(`Restored history #${idx + 1}`);
  };
  /** Whether the Pin-Layout naming Dialog is open + the working name. */
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinDraftName, setPinDraftName] = useState("");
  const openPinDialog = () => {
    setPinDraftName(`Checkpoint ${pinnedCheckpoints.length + 1}`);
    setPinDialogOpen(true);
  };
  const confirmPin = () => {
    const name = pinDraftName.trim();
    if (!name) return;
    const snap = {
      equipment_list:   (project.equipment_list   || []).map((x) => ({ ...x, position: x.position ? { ...x.position } : null })),
      cable_trays: (project.cable_trays || []).map((x) => ({ ...x, points: x.points ? x.points.map((p) => [...p]) : x.points })),
    };
    const entry = { name, snap, ts: Date.now(),
      summary: {
        items: snap.equipment_list.filter((x) => x.position).length,
        trays: snap.cable_trays.length,
      } };
    setPinnedCheckpoints((prev) => [...prev, entry]);
    setPinDialogOpen(false);
    toast.success(`Pinned "${name}"`);
  };
  const pinCurrentLayout = () => openPinDialog();
  const restorePinned = (idx) => {
    const entry = pinnedCheckpoints[idx];
    if (!entry) return;
    update(entry.snap);
    setRedoStack([]);
    toast.success(`Restored "${entry.name}"`);
  };
  const removePinned = (idx) => {
    const entry = pinnedCheckpoints[idx];
    if (!entry) return;
    setPinnedCheckpoints((prev) => prev.filter((_, i) => i !== idx));
    toast.success(`Removed "${entry.name}"`);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        dispatchSelection("clear");
        setMaximizedWallId(null);
      }
      // Cmd/Ctrl+Z = undo · Cmd/Ctrl+Shift+Z = redo
      if ((e.key === "z" || e.key === "Z") && (e.metaKey || e.ctrlKey)) {
        const t = e.target;
        const tag = (t?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
        e.preventDefault();
        if (e.shiftKey) applyRedo();
        else            applyUndo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [applyUndo, applyRedo]);

  // E2E hook — Playwright can call window.__elev_select_for_test(wallId, id)
  // to drive multi-select inside any wall panel. Mirrors window.__plan_*.
  useEffect(() => {
    window.__elev_select_for_test = (wallId, id) =>
      dispatchSelection({ wallId, ids: new Set([id]) });
    window.__elev_maximize_for_test = (wallId) => setMaximizedWallId(wallId);
    return () => {
      delete window.__elev_select_for_test;
      delete window.__elev_maximize_for_test;
    };
  }, []);

  if (!L || !W || !H) {
    return (
      <div className="text-gray-500 text-sm font-mono py-8 text-center"
           data-testid="elevation-views-empty">
        Enter room length, width and height to enable elevation views.
      </div>
    );
  }

  /** Defer the parent `update()` call to a setTimeout(0) macrotask so it
   *  runs OUTSIDE the synchronous child event-handler tick. Combined with
   *  the `snapshot()` deferral above this fully decouples ElevationViews's
   *  local state updates from the parent ProjectBuilder setProject() call
   *  and silences React 18's cross-component "cannot update X while
   *  rendering Y" warning. Centralised so all seven mutation paths stay
   *  consistent — if a future handler forgets to defer it'll regress. */
  const deferUpdate = (patch) => setTimeout(() => update(patch), 0);

  const createDrop = ({ wallId, offsetAlongWall, top_in, bottom_in }) => {
    snapshot();
    /* v1.78 — flush-against-wall margin = trayWidth/2 + railThickness/2 so
     * the outer rail edge in 3D sits exactly at the wall surface instead
     * of poking 0.5" outside the enclosure. Was: (trayWidth/2) only. */
    const margin = wallFlushMargin(trayDefaults.width_in || 12);
    const [x, y] = wallToPlan(wallId, offsetAlongWall, L, W, margin);
    const drop = {
      id: genId(),
      kind: "drop",
      points: [[Math.round(x), Math.round(y)]],
      width_in:  trayDefaults.width_in  || 12,
      height_in: trayDefaults.height_in || 4,
      tray_type: trayDefaults.tray_type || "Ladder",
      material:  trayDefaults.material  || "Aluminum",
      fill_limit_pct: trayDefaults.fill_limit_pct || 50,
      top_in:    Math.round(top_in),
      bottom_in: Math.round(bottom_in),
      elevation_in: Math.round((top_in + bottom_in) / 2),
      wall: wallId,
      installation_side: newItemSide,
    };
    deferUpdate({ cable_trays: [...(project.cable_trays || []), drop] });
  };

  /** Create a horizontal tray run along a wall at a fixed elevation.
   *  Both endpoints sit on the same wall axis — the run hugs the wall. */
  const createRun = ({ wallId, startOffset, endOffset, elevation_in }) => {
    snapshot();
    /* v1.78 — flush margin: trayW/2 + railW/2. See createDrop above. */
    const margin = wallFlushMargin(trayDefaults.width_in || 12);
    const [x1, y1] = wallToPlan(wallId, startOffset, L, W, margin);
    const [x2, y2] = wallToPlan(wallId, endOffset,   L, W, margin);
    const run = {
      id: genId(),
      kind: "run",
      points: [[Math.round(x1), Math.round(y1)], [Math.round(x2), Math.round(y2)]],
      width_in:  trayDefaults.width_in  || 12,
      height_in: trayDefaults.height_in || 4,
      tray_type: trayDefaults.tray_type || "Ladder",
      material:  trayDefaults.material  || "Aluminum",
      fill_limit_pct: trayDefaults.fill_limit_pct || 50,
      elevation_in: Math.max(0, Math.round(elevation_in)),
      wall: wallId,
      installation_side: newItemSide,
    };
    deferUpdate({ cable_trays: [...(project.cable_trays || []), run] });
    toast.success(`Tray run added @ ${Math.round(elevation_in)}″ on Wall ${wallId}${newItemSide === "exterior" ? " (exterior)" : ""}`);
  };

  /** Patch a single drop. `_moveTo: {wallId, offset}` is a special key —
   *  if present we recompute the drop's plan (x,y) for the new wall+offset
   *  and remove the marker before persisting. */
  const updateDrop = (id, patch) => {
    snapshot();
    const next = allTrays.map((t) => {
      if (t.id !== id) return t;
      const out = { ...t, ...patch };
      if (patch._moveTo) {
        const margin = wallFlushMargin(t.width_in || 12);
        const [x, y] = wallToPlan(patch._moveTo.wallId, patch._moveTo.offset, L, W, margin);
        out.points = [[Math.round(x), Math.round(y)]];
        out.wall = patch._moveTo.wallId;
        delete out._moveTo;
      }
      if (patch.top_in != null || patch.bottom_in != null) {
        out.elevation_in = Math.round(((out.top_in || H) + (out.bottom_in || 0)) / 2);
      }
      if (out.top_in != null && out.bottom_in != null && out.top_in < out.bottom_in) {
        const swap = out.top_in; out.top_in = out.bottom_in; out.bottom_in = swap;
      }
      return out;
    });
    deferUpdate({ cable_trays: next });
  };

  const removeDrop = (id) => { snapshot(); deferUpdate({ cable_trays: allTrays.filter((t) => t.id !== id) }); };
  const updateRun  = (id, patch) => {
    snapshot();
    const next = allTrays.map((t) => {
      if (t.id !== id) return t;
      const out = { ...t, ...patch };
      // v1.94 — _moveEndpoint: { which: "A"|"B", wallId, offset } recomputes
      // the corresponding endpoint's (x_in, y_in) in plan space, leaving
      // the other endpoint untouched. Used by the elevation-view A/B
      // endpoint bubbles to let users adjust the horizontal extent of a
      // tray run without leaving the elevation panel.
      if (patch._moveEndpoint && Array.isArray(out.points) && out.points.length >= 2) {
        const { which, wallId, offset } = patch._moveEndpoint;
        const margin = wallFlushMargin(out.width_in || 12);
        const [nx, ny] = wallToPlan(wallId, offset, L, W, margin);
        const idx = which === "A" ? 0 : 1;
        const newPts = out.points.map((p, i) => i === idx ? [Math.round(nx), Math.round(ny)] : p);
        // Ensure A < B along the wall axis after the move so downstream
        // wallRuns derivation keeps Math.min/Math.max stable.
        out.points = newPts;
        out.wall = wallId;
        delete out._moveEndpoint;
      }
      return out;
    });
    deferUpdate({ cable_trays: next });
  };

  // ── Equipment placement / movement (from Equipment List) ─────────────

  const placePickedItem = ({ wallId, offsetAlongWall, mountElevation }) => {
    if (!pickedItem) return;
    snapshot();
    const pos = positionForWall(wallId, offsetAlongWall,
                                  pickedItem.length_in, pickedItem.width_in, L, W);
    const next = allLoadList.map((it) => it.id !== pickedItem.id ? it : {
      ...it,
      mount_height_in: Math.max(0, Math.round(mountElevation)),
      mount_surface: `wall_${wallId.toLowerCase()}`,
      installation_side: newItemSide,
      position: { x_in: Math.round(pos.x_in), y_in: Math.round(pos.y_in),
                    w_in: pos.w_in,             h_in: pos.h_in },
    });
    deferUpdate({ equipment_list: next });
    toast.success(
      `Placed ${pickedItem.tag} on Wall ${wallId} @ ${Math.round(mountElevation)}″${newItemSide === "exterior" ? " (exterior)" : ""}`,
    );
    setPickedItemId("");
  };

  const updateEquipment = (id, { wallId, offsetAlongWall, mountElevation, patch: extraPatch }) => {
    const it = allLoadList.find((x) => x.id === id);
    if (!it) return;
    snapshot();
    // v1.71 — use POST-PATCH orientedDimensions so the wall-horizontal extent
    // (wallSize) and wall-perpendicular extent (thickness) reflect the flip
    // the user just performed. Was: passing raw length_in/width_in here so
    // pos.w_in/h_in got reset to PRE-flip values, which then disagreed with
    // axis_map until the backend normalize ran on autosave.
    const merged = { ...it, ...(extraPatch || {}) };
    const _od = orientedDimensions(merged);
    const pos = positionForWall(wallId, offsetAlongWall, _od.wallSize, _od.thickness, L, W);
    const next = allLoadList.map((x) => x.id !== id ? x : {
      ...x,
      ...(extraPatch || {}),
      mount_height_in: Math.max(0, Math.round(mountElevation)),
      mount_surface: `wall_${wallId.toLowerCase()}`,
      position: { x_in: Math.round(pos.x_in), y_in: Math.round(pos.y_in),
                    w_in: pos.w_in,             h_in: pos.h_in },
    });
    deferUpdate({ equipment_list: next });
  };

  const updateEquipmentGroup = (updates) => {
    if (!updates || updates.length === 0) return;
    snapshot();
    const byId = new Map(updates.map((u) => [u.id, u]));
    const next = allLoadList.map((x) => {
      const u = byId.get(x.id);
      if (!u) return x;
      const _od = orientedDimensions(x);
      const pos = positionForWall(u.wallId, u.offsetAlongWall, _od.wallSize, _od.thickness, L, W);
      return {
        ...x,
        mount_height_in: Math.max(0, Math.round(u.mountElevation)),
        mount_surface: `wall_${u.wallId.toLowerCase()}`,
        position: { x_in: Math.round(pos.x_in), y_in: Math.round(pos.y_in),
                      w_in: pos.w_in,             h_in: pos.h_in },
      };
    });
    deferUpdate({ equipment_list: next });
    toast.success(`Moved ${updates.length} items`);
  };

  return (
    <div className="mt-6 border border-white/10 p-3 bg-[#0a0d12]"
         data-testid="elevation-views">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="label-tag">// Wall elevations</div>
          <div className="text-sm text-white">
            {mode === "drop"
              ? "Drag vertically = drop · drag horizontally = run · auto-detects."
              : mode === "run"
                ? "Drag horizontally across a wall to create a tray run at that elevation."
                : pickedItem
                  ? `Click any wall to place ${pickedItem.tag} · drag existing equipment to move.`
                  : "Select an unplaced equipment item below, then click a wall."}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap font-mono">
          <div className="relative inline-flex items-stretch border"
               style={{ borderColor: undoStack.length === 0 ? "rgba(255,255,255,0.1)" : "rgba(0,229,255,0.4)" }}
               data-testid="elevation-undo-wrap">
            <button type="button"
                    onClick={applyUndo}
                    disabled={undoStack.length === 0}
                    title={undoStack.length === 0
                      ? "No recent changes to undo"
                      : `Undo last change${typeof navigator !== "undefined" && /Mac/i.test(navigator.platform) ? " (⌘Z)" : " (Ctrl+Z)"}`}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs ${
                      undoStack.length === 0
                        ? "text-gray-600 cursor-not-allowed"
                        : "text-[#00E5FF] hover:bg-[#00E5FF]/10"
                    }`}
                    data-testid="elevation-undo-btn">
              <ArrowCounterClockwise size={12} weight="bold" />
              Undo {undoStack.length > 0 ? `(${undoStack.length}/${UNDO_LIMIT})` : ""}
            </button>
            <button type="button"
                    onClick={applyRedo}
                    disabled={redoStack.length === 0}
                    title={redoStack.length === 0
                      ? "Nothing to redo"
                      : `Redo${typeof navigator !== "undefined" && /Mac/i.test(navigator.platform) ? " (⌘⇧Z)" : " (Ctrl+Shift+Z)"}`}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs border-l ${
                      redoStack.length === 0
                        ? "border-white/5 text-gray-600 cursor-not-allowed"
                        : "border-[#00E5FF]/20 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                    }`}
                    data-testid="elevation-redo-btn">
              <ArrowClockwise size={12} weight="bold" />
              Redo {redoStack.length > 0 ? `(${redoStack.length})` : ""}
            </button>
            <button type="button"
                    onClick={() => setHistoryOpen((v) => !v)}
                    title={historyOpen ? "Hide history rail" : "Show history rail on the right"}
                    className={`inline-flex items-center px-1 border-l text-xs border-[#00E5FF]/20 ${
                      historyOpen
                        ? "bg-[#00E5FF]/15 text-[#00E5FF]"
                        : "text-gray-400 hover:text-[#00E5FF] hover:bg-[#00E5FF]/10"
                    }`}
                    data-testid="elevation-history-toggle">
              <CaretDown size={11} weight={historyOpen ? "fill" : "regular"} />
            </button>
          </div>
          <button type="button"
                  onClick={pinCurrentLayout}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#FFCC00]/40 text-[#FFCC00] hover:bg-[#FFCC00]/10"
                  title="Save current layout as a named checkpoint (independent of the 5-deep undo stack)"
                  data-testid="elevation-pin-btn">
            <PushPin size={12} weight="fill" />
            Pin layout
          </button>
          <div className="inline-flex border border-white/10"
               data-testid="elevation-mode-toggle">
            <button type="button" onClick={() => setMode("drop")}
                    className={`px-3 py-1 text-xs ${
                      mode === "drop"
                        ? "bg-[#FFCC00] text-black font-bold"
                        : "text-gray-400 hover:text-white"
                    }`}
                    title="Draw trays — direction of drag picks drop (vertical) or run (horizontal)."
                    data-testid="elevation-mode-drop">
              Tray / conduit
            </button>
            <button type="button" onClick={() => setMode("run")}
                    className={`px-3 py-1 text-xs border-l border-white/10 ${
                      mode === "run"
                        ? "bg-[#22c55e] text-black font-bold"
                        : "text-gray-400 hover:text-white"
                    }`}
                    title="Drag horizontally across a wall to add a tray run at that elevation"
                    data-testid="elevation-mode-run">
              Tray run
            </button>
            <button type="button" onClick={() => setMode("equipment")}
                    className={`px-3 py-1 text-xs border-l border-white/10 ${
                      mode === "equipment"
                        ? "bg-[#00E5FF] text-black font-bold"
                        : "text-gray-400 hover:text-white"
                    }`}
                    data-testid="elevation-mode-equipment">
              Equipment
            </button>
          </div>
          {mode === "equipment" && (
            unplaced.length === 0 ? (
              <div className="text-[10px] text-yellow-300 max-w-xs">
                All equipment-list items are placed. Add new equipment in the
                Equipment List tab first — the equipment list is the source of truth.
              </div>
            ) : (
              <select value={pickedItemId}
                      onChange={(e) => setPickedItemId(e.target.value)}
                      className="bg-black/40 border border-white/10 px-2 py-1 text-xs"
                      data-testid="elevation-picked-item">
                <option value="">— pick unplaced item ({unplaced.length}) —</option>
                {unplaced.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.tag} · {it.equipment_type || "?"}
                    {it.length_in ? ` · ${it.length_in}×${it.width_in}×${it.depth_in || "?"}″` : ""}
                  </option>
                ))}
              </select>
            )
          )}
          <div className="text-[10px] text-gray-500">
            {drops.length} drop{drops.length === 1 ? "" : "s"} ·{" "}
            {equipment.length} placed · {unplaced.length} unplaced
            {selection.ids.size > 0 && (
              <span className="text-[#FFCC00]">
                {" "}· {selection.ids.size} selected (Esc to clear)
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="text-[10px] text-gray-500 font-mono mb-2 -mt-1">
        Tip: <kbd className="px-1 border border-white/20 text-[9px]">Shift</kbd>+click
        equipment to add to selection · drag any selected item to move the
        whole group together · click the ⤢ button on any wall header to
        enlarge it.
      </div>
      <div className="flex gap-3">
        <div className="flex-1 min-w-0">
          {maximizedWallId
            ? (() => {
                const w = WALLS.find((x) => x.id === maximizedWallId) || WALLS[0];
                return (
                  <WallPanel key={`max-${w.id}`} wall={w}
                    L={L} W={W} H={H}
                    openings={openings} drops={drops} runs={runs}
                    equipment={equipment}
                    mode={mode} pickedItem={pickedItem}
                    selectedIds={selection.wallId === w.id ? selection.ids : new Set()}
                    onSelectionChange={(s) => dispatchSelection(s)}
                    onCreateDrop={createDrop}
                    onCreateRun={createRun}
                    onPlacePickedItem={placePickedItem}
                    onUpdateDrop={updateDrop}
                    onRemoveDrop={removeDrop}
                    onUpdateRun={updateRun}
                    onUpdateEquipment={updateEquipment}
                    onUpdateEquipmentGroup={updateEquipmentGroup}
                    isMaximized={true}
                    onMaximize={() => setMaximizedWallId(null)} />
                );
              })()
            : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {WALLS.map((w) => (
                  <WallPanel key={w.id} wall={w}
                    L={L} W={W} H={H}
                    openings={openings} drops={drops} runs={runs}
                    equipment={equipment}
                    mode={mode} pickedItem={pickedItem}
                    selectedIds={selection.wallId === w.id ? selection.ids : new Set()}
                    onSelectionChange={(s) => dispatchSelection(s)}
                    onCreateDrop={createDrop}
                    onCreateRun={createRun}
                    onPlacePickedItem={placePickedItem}
                    onUpdateDrop={updateDrop}
                    onRemoveDrop={removeDrop}
                    onUpdateRun={updateRun}
                    onUpdateEquipment={updateEquipment}
                    onUpdateEquipmentGroup={updateEquipmentGroup}
                    isMaximized={false}
                    onMaximize={() => setMaximizedWallId(w.id)} />
                ))}
                {/* v1.99 — ROOF PANEL. Visible in "Exterior" or "All" mode
                    only. A top-down view of the roof surface where the
                    user can place equipment / trays that sit ABOVE the
                    enclosure (HVAC condensers, exhaust fans, conduits
                    crossing the building). Items placed here are stored
                    with `mount_surface: "roof"` + `installation_side:
                    "exterior"`. */}
                {(installSide === "exterior" || installSide === "all") && (
                  <RoofPanel L={L} W={W} project={project}
                    equipment={equipment.filter((e) => e.mount_surface === "roof")}
                    pickedItem={pickedItem} mode={mode}
                    onPlace={({ x_in, y_in }) => {
                      if (!pickedItem) return;
                      snapshot();
                      const od = orientedDimensions(pickedItem);
                      const next = allLoadList.map((it) => it.id !== pickedItem.id ? it : {
                        ...it,
                        mount_height_in: H,
                        mount_surface: "roof",
                        installation_side: "exterior",
                        position: { x_in: Math.round(x_in), y_in: Math.round(y_in),
                                    w_in: od.wallSize, h_in: od.thickness },
                      });
                      deferUpdate({ equipment_list: next });
                      toast.success(`Placed ${pickedItem.tag} on ROOF (exterior)`);
                      setPickedItemId("");
                    }} />
                )}
              </div>
            )}
        </div>
        {historyOpen && (
          <HistoryRail
            L={L} W={W} UNDO_LIMIT={UNDO_LIMIT}
            undoStack={undoStack}
            jumpToHistory={jumpToHistory}
            pinnedCheckpoints={pinnedCheckpoints}
            restorePinned={restorePinned}
            removePinned={removePinned}
            onClose={() => setHistoryOpen(false)} />
        )}
      </div>
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent className="bg-[#0a0d12] border border-[#FFCC00]/40 font-mono"
                       data-testid="elevation-pin-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FFCC00]">📌 Pin layout checkpoint</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-gray-400 mb-2">
            Save the current arrangement as a named checkpoint — independent of
            the 5-deep rolling undo stack, so you can recall it any time.
          </div>
          <Input autoFocus value={pinDraftName}
                 onChange={(e) => setPinDraftName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") confirmPin(); }}
                 placeholder="e.g. After Bob's review"
                 className="bg-black/40 border-white/15 focus:border-[#FFCC00]"
                 data-testid="elevation-pin-dialog-name" />
          <DialogFooter className="mt-3 gap-2">
            <button type="button"
                    onClick={() => setPinDialogOpen(false)}
                    className="px-3 py-1 text-xs border border-white/15 text-gray-300 hover:text-white"
                    data-testid="elevation-pin-dialog-cancel">
              Cancel
            </button>
            <button type="button"
                    onClick={confirmPin}
                    disabled={!pinDraftName.trim()}
                    className="px-3 py-1 text-xs border border-[#FFCC00] bg-[#FFCC00]/10 text-[#FFCC00] hover:bg-[#FFCC00]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="elevation-pin-dialog-save">
              Pin checkpoint
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * RoofPanel — v1.99
 * A simple top-down view of the enclosure ROOF. Used to place equipment
 * that sits ABOVE the building (HVAC condensers, exhaust fans, lightning
 * arresters, conduit fly-overs). All placements stamp `mount_surface:
 * "roof"` and `installation_side: "exterior"` automatically.
 *
 * The panel mirrors the plan-view scale so layouts read consistently
 * across surfaces. Equipment renders as a green-bordered slate rect so
 * it visually echoes the shell's "structural-but-flexible" palette.
 */
function RoofPanel({ L, W, project, equipment, pickedItem, mode, onPlace }) {
  const padding = 24;
  const viewW = 420;
  const scale = (viewW - padding * 2) / Math.max(1, L);
  const viewH = W * scale + padding * 2;
  const toRoom = (sx, sy) => ({
    x_in: Math.max(0, Math.min(L, (sx - padding) / scale)),
    y_in: Math.max(0, Math.min(W, W - (sy - padding) / scale)),
  });
  const handleClick = (e) => {
    if (mode !== "equipment" || !pickedItem) return;
    const svg = e.currentTarget;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const local = pt.matrixTransform(svg.getScreenCTM().inverse());
    const { x_in, y_in } = toRoom(local.x, local.y);
    onPlace({ x_in, y_in });
  };
  const cornerPx = 7;
  return (
    <div className="border border-white/10 bg-[#050608] p-2"
         data-testid="elevation-panel-ROOF">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#22c55e]">
          // ROOF (top view) · {Math.round(L)}″ × {Math.round(W)}″
        </div>
        <div className="text-[9px] text-gray-500">
          {equipment.length} on roof
        </div>
      </div>
      <svg width={viewW} height={viewH}
           viewBox={`0 0 ${viewW} ${viewH}`}
           onClick={handleClick}
           style={{ cursor: pickedItem ? "crosshair" : "default" }}
           data-testid="elevation-panel-ROOF-canvas">
        {/* roof outline (slate, like the shell) */}
        <rect x={padding} y={padding}
              width={L * scale} height={W * scale}
              fill="#3B475A" fillOpacity="0.4"
              stroke="#22c55e" strokeWidth="1.5"
              strokeDasharray="6 3" />
        {/* green corner datums */}
        {[
          [padding,                   padding],
          [padding + L * scale - cornerPx, padding],
          [padding + L * scale - cornerPx, padding + W * scale - cornerPx],
          [padding,                   padding + W * scale - cornerPx],
        ].map(([cx, cy], i) => (
          <rect key={i} x={cx} y={cy} width={cornerPx} height={cornerPx}
                fill="#22c55e" pointerEvents="none" />
        ))}
        {/* equipment on roof */}
        {equipment.map((it) => {
          const p = it.position;
          if (!p) return null;
          const rectW = (p.w_in || 12) * scale;
          const rectH = (p.h_in || 12) * scale;
          const tlX = padding + p.x_in * scale;
          const tlY = padding + (W - p.y_in - (p.h_in || 12)) * scale;
          return (
            <g key={it.id} pointerEvents="none"
               data-testid={`roof-equip-${it.id}`}>
              <rect x={tlX} y={tlY} width={rectW} height={rectH}
                    fill="#FF9F0A" fillOpacity="0.25"
                    stroke="#22c55e" strokeWidth="1.2" />
              <text x={tlX + 3} y={tlY + 11}
                    fill="#fff" fontFamily="JetBrains Mono"
                    fontSize="9" pointerEvents="none">
                {it.tag || it.equipment_type?.slice(0, 8) || "EQ"}
              </text>
            </g>
          );
        })}
        {/* placement hint */}
        {mode === "equipment" && pickedItem && (
          <text x={viewW / 2} y={viewH - 6}
                fill="#22c55e" fontFamily="JetBrains Mono"
                fontSize="9" textAnchor="middle" opacity="0.8">
            Click anywhere on the roof to place {pickedItem.tag}
          </text>
        )}
      </svg>
    </div>
  );
}
